import { useEffect, useRef } from "react";

/**
 * Calls `fn` on mount and then every `delay` ms while the tab is visible.
 * Pauses polling when the browser tab is hidden to save bandwidth.
 */
export default function useAutoRefresh(fn, delay = 15000, deps = []) {
  const savedFn = useRef(fn);

  useEffect(() => {
    savedFn.current = fn;
  }, [fn]);

  useEffect(() => {
    let timer = null;

    const tick = () => {
      if (document.visibilityState === "visible") {
        try { savedFn.current?.(); } catch { /* ignore */ }
      }
    };

    const start = () => {
      if (timer) return;
      timer = setInterval(tick, delay);
    };
    const stop = () => {
      if (timer) { clearInterval(timer); timer = null; }
    };

    start();
    const onVis = () => {
      if (document.visibilityState === "visible") {
        tick(); // refresh immediately when tab becomes visible again
        start();
      } else {
        stop();
      }
    };
    document.addEventListener("visibilitychange", onVis);
    return () => {
      stop();
      document.removeEventListener("visibilitychange", onVis);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [delay, ...deps]);
}
